/**
 * Path helpers for the AtsVendorConfiguration document body. Documents were seeded
 * by different writers over time, so property casing varies (the processor reads
 * case-insensitively with Newtonsoft). Reads match property names case-insensitively
 * at every step; writes reuse the existing property (whatever its casing) and only
 * create camelCase properties when absent — so a PascalCase legacy document never
 * ends up with both `AtsSource` and `atsSource`. Unknown properties always ride
 * along untouched: the document is cloned, never rebuilt.
 */

type Doc = Record<string, any>

function findKey(obj: Doc, name: string): string | undefined {
  if (Object.prototype.hasOwnProperty.call(obj, name)) return name
  const lower = name.toLowerCase()
  return Object.keys(obj).find((key) => key.toLowerCase() === lower)
}

export function getPath(doc: Doc | null | undefined, path: string[]): any {
  let current: any = doc
  for (const segment of path) {
    if (current === null || current === undefined || typeof current !== "object") return undefined
    const key = findKey(current, segment)
    if (key === undefined) return undefined
    current = current[key]
  }
  return current
}

/**
 * Array read for form bindings: a hand-pasted document (JSON tab) can hold any
 * value at any path, so non-arrays normalize to [] instead of crashing .map/.filter.
 */
export function getArray(doc: Doc | null | undefined, path: string[]): any[] {
  const value = getPath(doc, path)
  return Array.isArray(value) ? value : []
}

/** String read for form bindings: non-strings (pasted JSON) normalize to "". */
export function getString(doc: Doc | null | undefined, path: string[]): string {
  const value = getPath(doc, path)
  return typeof value === "string" ? value : ""
}

/** Returns a new document with `value` set at `path`; intermediate objects are created. */
export function setPath(doc: Doc, path: string[], value: any): Doc {
  const result = { ...doc }
  let current: Doc = result
  for (let i = 0; i < path.length - 1; i++) {
    const key = findKey(current, path[i]) ?? path[i]
    const child = current[key]
    current[key] = child !== null && child !== undefined && typeof child === "object" && !Array.isArray(child) ? { ...child } : {}
    current = current[key]
  }
  const leafKey = findKey(current, path[path.length - 1]) ?? path[path.length - 1]
  current[leafKey] = value
  return result
}

/**
 * A new configuration document prefilled with the processor model's defaults.
 * atsSourceId/atsSourceName are canonicalized server-side from the chosen source.
 */
export function createDefaultDocument(atsSource: string): Record<string, any> {
  return {
    atsSource,
    atsConfiguration: {
      atsSourceId: "",
      atsSourceName: "",
      requiresVendorNameExtraction: false,
      vendorNameExtractionPatterns: [],
      allowedEntityEventTypes: ["UPDATED"],
      additionalEntityMappings: [],
      useWorkerNameAsOrganization: false,
      createDraftOrganizationOnly: false,
      clientCorporationFilter: null,
      linesOfBusinessAllowlist: [],
      enableManualPlacementReview: false,
      autoCreateSupplierContact: false,
    },
    statusMappings: {
      workOrderStatuses: { active: [], inactive: [] },
      organizationStatuses: { active: [], inactive: [] },
      profileStatuses: { active: [], inactive: [] },
    },
    businessRules: {
      clientOrganizations: {
        defaults: {
          useCountryOfRegistrationFromAddress: false,
          useDefaultTaxProvinceFromAddress: false,
          defaultOrganizationTypeCode: null,
          defaultSalesTaxTerritoryCode: null,
        },
      },
      countryCodeMappings: { roleTypeMappings: {}, defaultCountryCode: "US" },
      roleIdMappings: { roleTypeToRoleId: {}, defaultRoleId: 1 },
      placementTypeMappings: { skippedPlacementTypes: [], permanentPlacementTypes: [] },
      profileTypeCodeRules: [],
      subdivisionMappings: { mappings: {} },
      assignmentDefaults: {},
      workOrderTemplateMappings: { workerTypeToTemplateId: {} },
      lineOfBusinessIdMappings: { lineOfBusinessCodeToId: {} },
      workerLocationTypeMappings: { workerLocationTypeCodeToId: {} },
      restrictPaymentTermsToWorkerTypes: [],
      engagementEligibleLinesOfBusinessIds: [],
    },
  }
}
